// MyTasksScreen - §14 담당자 개인화면.
//
// 읽는 화면이 아니라 **처리하는 화면**이다. §14 는 "별도의 일일보고 작성
// 보다는 해당 업무의 완료 여부만 업데이트" 를 요구하므로, 이 화면에는
// 자유 서술 입력 필드가 없다 (REQ-MY-005 AC2). 지연 행의 [업데이트] 만
// 사유 폼을 여는데, 그건 시스템이 이미 지연으로 판정한 뒤의 후속 입력이다.
//
// 블록 순서는 **지연 -> 오늘 -> 금주** 다 (결정 S-3). §14 원문은
// `오늘 -> 금주 -> 지연` 이지만 지연이 화면 하단에 있으면 스크롤 없이
// 안 보이고, 그건 §11·§20 의 "문제 있는 것 우선" 과 정면으로 어긋난다.
import { IssueForm, type IssueTarget } from "@/components/IssueForm";
import { StatusBadge } from "@/components/StatusIndicator";
import {
  Button,
  Card,
  DataBoundary,
  EmptyState,
  ProgressBar,
  Select,
  Skeleton,
} from "@/components/ui";
import {
  type Me,
  type MyTask,
  type Staff,
  completeMilestone,
  completePrepItem,
  completeProductionPlan,
  uncompleteMilestone,
  updatePrepItem,
  updateProductionPlan,
} from "@/lib/api";
import { asOfLabel, monthDay } from "@/lib/dates";
import { EMPTY } from "@/lib/format";
import { useMyTasks } from "@/lib/hooks";
import { statusKey } from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

const taskKey = (t: MyTask): string => `${t.kind}:${t.targetId}`;

/** 완료 처리와 그 역연산. 대상 종류마다 엔드포인트가 다르다 (REQ-MY-005). */
const complete = (t: MyTask): Promise<unknown> =>
  t.kind === "milestone"
    ? completeMilestone(t.targetId)
    : t.kind === "prep"
      ? completePrepItem(t.targetId)
      : completeProductionPlan(t.targetId);

const undoComplete = (t: MyTask): Promise<unknown> =>
  t.kind === "milestone"
    ? uncompleteMilestone(t.targetId)
    : t.kind === "prep"
      ? updatePrepItem(t.targetId, { completedDate: "" })
      : updateProductionPlan(t.targetId, { isDone: false });

export const MyTasksScreen = ({
  nav,
  me,
  staff,
}: {
  nav: ScreenNav;
  me: Me | null;
  staff: readonly Staff[];
}) => {
  const isAdmin = me?.role === "admin";
  const [staffId, setStaffId] = useState("");
  const [done, setDone] = useState<readonly string[]>([]);
  const [target, setTarget] = useState<IssueTarget | null>(null);

  const res = useMyTasks(isAdmin && staffId ? staffId : undefined);
  const data = res.data;

  const finish = async (t: MyTask) => {
    setDone((prev) => [...prev, taskKey(t)]);
    try {
      await complete(t);
      // 확인 다이얼로그 대신 5초 되돌리기다 (결정 S-10).
      nav.toastUndo("완료 처리했어요.", async () => {
        await undoComplete(t);
        setDone((prev) => prev.filter((k) => k !== taskKey(t)));
        void res.reload();
      });
    } catch (err) {
      setDone((prev) => prev.filter((k) => k !== taskKey(t)));
      nav.toast(
        err instanceof Error ? err.message : "저장하지 못했어요. 잠시 후 다시 시도해 주세요.",
        "error"
      );
    }
  };

  const openIssue = (t: MyTask) =>
    setTarget({
      devItemId: t.devItemId,
      targetType: t.kind,
      targetId: t.targetId,
      targetLabel: t.taskLabel,
      issueId: null,
      reason: "",
      action: "",
      expectedDate: "",
    });

  const live = (rows: readonly MyTask[], exclude: readonly string[] = []) =>
    rows.filter((t) => !done.includes(taskKey(t)) && !exclude.includes(taskKey(t)));

  return (
    <DataBoundary
      loading={res.loading}
      error={res.error}
      onRetry={() => void res.reload()}
      skeleton={
        <div className="flex flex-col gap-s4">
          <Skeleton h={70} />
          <Skeleton h={120} />
          <Skeleton h={120} />
        </div>
      }
    >
      {!data ? null : !data.staffLinked && !isAdmin ? (
        // 담당자가 연결되지 않은 일반 사용자는 볼 것이 없다 (REQ-MY-008).
        // 관리자는 예외다 - 본인 계정이 담당자와 연결되지 않았어도 담당자를
        // 골라 남의 업무를 볼 수 있어야 한다 (REQ-MY-007).
        <Card>
          <EmptyState
            icon="user"
            title="담당자 정보가 연결되지 않았습니다"
            description="관리자에게 담당자 연결을 요청해 주세요. 연결되면 본인 담당 개발품과 업무가 여기 모여요."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-s4">
          <header className="flex flex-wrap items-end justify-between gap-s3">
            <div className="flex flex-col gap-s1">
              <h1 className="text-title">
                {data.staffLinked ? `${data.staffName} 님의 업무` : "담당자 업무 조회"}
              </h1>
              <p
                className="text-caption tabular-nums"
                data-num=""
                style={{ color: "var(--text-tertiary)" }}
              >
                {asOfLabel(data.asOf)}
              </p>
            </div>
            {/* 관리자만 다른 담당자의 화면을 볼 수 있다 (REQ-MY-007). */}
            {isAdmin && (
              <span className="w-[11rem]">
                <Select
                  value={staffId}
                  onChange={setStaffId}
                  options={staff
                    .filter((s) => s.active)
                    .map((s) => ({ value: s.id, label: s.name }))}
                  ariaLabel="담당자 업무 조회"
                  placeholder="담당자 선택"
                />
              </span>
            )}
          </header>

          {!data.staffLinked && (
            <Card>
              <EmptyState
                icon="user"
                title="담당자를 선택해 주세요"
                description="내 계정에 담당자가 연결되어 있지 않아요. 위에서 담당자를 고르면 그 담당자의 업무가 보이고, 마스터에서 내 계정을 담당자와 연결할 수 있어요."
                action={
                  <Button variant="secondary" size="md" onClick={() => nav.goto("master")}>
                    마스터로 이동
                  </Button>
                }
              />
            </Card>
          )}

          {/* 지연 0건이면 이 블록 자체를 렌더하지 않는다. 정상 진행 건은
              알림하지 않는다 (§15 준용). */}
          {data.staffLinked && live(data.delayed).length > 0 && (
            <TaskBlock
              title="지연"
              count={live(data.delayed).length}
              tone="late"
              rows={live(data.delayed)}
              onOpenItem={nav.openItem}
              onFinish={finish}
              onIssue={openIssue}
            />
          )}

          {data.staffLinked && (
            <>
              <TaskBlock
                title="오늘 예정"
                count={live(data.todayTasks, data.delayed.map(taskKey)).length}
                rows={live(data.todayTasks, data.delayed.map(taskKey))}
                emptyText="오늘 처리할 항목이 없어요. 편하게 쉬셔도 좋아요."
                onOpenItem={nav.openItem}
                onFinish={finish}
              />

              <TaskBlock
                title="금주 예정"
                count={live(data.weekTasks, data.delayed.map(taskKey)).length}
                rows={live(data.weekTasks, data.delayed.map(taskKey))}
                showDate
                emptyText="금주 예정된 업무가 없어요."
                onOpenItem={nav.openItem}
                onFinish={finish}
              />

              <Card>
                <h2 className="text-heading">{`내 담당 개발품 ${data.myItems.length}건`}</h2>
                {data.myItems.length === 0 ? (
                  <p className="mt-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                    아직 배정된 개발품이 없어요. 관리자에게 배정을 요청해 주세요.
                  </p>
                ) : (
                  <ul className="mt-s3 grid list-none grid-cols-1 gap-s3 p-0 md:grid-cols-2">
                    {data.myItems.map((i) => (
                      <li
                        key={i.id}
                        className="flex items-center justify-between gap-s3 rounded-control px-s3 py-s2"
                        style={{ background: "var(--surface-inset)" }}
                      >
                        <span className="flex min-w-0 flex-col">
                          <Button variant="link" size="sm" onClick={() => nav.openItem(i.id)}>
                            {`${i.customerName} ${i.partName}`}
                          </Button>
                          <span
                            className="text-micro tabular-nums"
                            data-num=""
                            style={{ color: "var(--text-tertiary)" }}
                          >
                            {`SOP ${monthDay(i.sopDate) || EMPTY} · ${i.currentStage?.label ?? EMPTY}`}
                          </span>
                        </span>
                        <span className="flex items-center gap-s3">
                          <span className="w-[4.5rem]">
                            <ProgressBar value={i.progress} />
                          </span>
                          <StatusBadge status={statusKey(i.status)} size="sm" />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </>
          )}
        </div>
      )}

      <IssueForm
        target={target}
        onClose={() => setTarget(null)}
        onSaved={(m) => {
          nav.toast(m);
          void res.reload();
        }}
        onError={(m) => nav.toast(m, "error")}
      />
    </DataBoundary>
  );
};

/** 44px 목록 행. 테이블이 아니다 - 항목이 보통 10개 미만이고 각 행에 버튼이 있다. */
const TaskBlock = ({
  title,
  count,
  rows,
  tone = "default",
  showDate = false,
  emptyText,
  onOpenItem,
  onFinish,
  onIssue,
}: {
  title: string;
  count: number;
  rows: readonly MyTask[];
  tone?: "default" | "late";
  showDate?: boolean;
  emptyText?: string;
  onOpenItem: (id: string) => void;
  onFinish: (t: MyTask) => void | Promise<void>;
  onIssue?: (t: MyTask) => void;
}) => (
  <Card pad="sm">
    <div className="flex items-baseline gap-s2 px-s2 pb-s2">
      <h2 className="text-heading">{title}</h2>
      <span
        className="text-caption tabular-nums"
        data-num=""
        style={{ color: "var(--text-tertiary)" }}
      >
        {`${count}건`}
      </span>
    </div>
    {rows.length === 0 ? (
      <p className="px-s2 pb-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
        {emptyText}
      </p>
    ) : (
      <ul className="flex list-none flex-col p-0">
        {rows.map((t) => (
          <li
            key={taskKey(t)}
            className="flex min-h-[2.75rem] flex-wrap items-center gap-s3 px-s2"
            style={{
              borderTop: "1px solid var(--border-subtle)",
              background: tone === "late" ? "var(--row-late-bg)" : undefined,
              boxShadow: tone === "late" ? "inset 3px 0 0 0 var(--state-late)" : undefined,
            }}
          >
            {tone === "late" && (
              <StatusBadge status="late" size="sm" label={`${t.delayDays}일 지연`} />
            )}
            {showDate && (
              <span className="tabular-nums" data-num="" style={{ color: "var(--text-tertiary)" }}>
                {monthDay(t.plannedDate) || EMPTY}
              </span>
            )}
            <Button variant="link" size="sm" onClick={() => onOpenItem(t.devItemId)}>
              {`${t.customerName} ${t.partName}`}
            </Button>
            <span className="text-label" style={{ color: "var(--text-secondary)" }}>
              {t.taskLabel}
            </span>
            {tone === "late" && (
              <span
                className="text-micro tabular-nums"
                data-num=""
                style={{ color: "var(--text-tertiary)" }}
              >
                {`계획 ${monthDay(t.plannedDate) || EMPTY}`}
              </span>
            )}
            <span className="ml-auto flex items-center gap-s2">
              {onIssue && !t.hasIssueDoc && (
                <Button variant="secondary" size="sm" onClick={() => onIssue(t)}>
                  사유 입력
                </Button>
              )}
              {onIssue && t.hasIssueDoc && (
                <Button variant="secondary" size="sm" onClick={() => onIssue(t)}>
                  업데이트
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={() => void onFinish(t)}>
                완료 처리
              </Button>
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);
